import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import Input from '../Common/Input';

const GlobalAssumptions = () => {
  const { inputs, updateInput } = useCalculator();

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // Handle facilityType as string
    if (name === 'facilityType') {
      updateInput(name, value);
    }
    // Handle facilityComplexity as number (even though it's a select)
    else if (name === 'facilityComplexity') {
      updateInput(name, parseInt(value) || 1);
    }
    // Handle all number inputs
    else {
      updateInput(name, parseFloat(value) || 0);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">🏥 Facility Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Facility Type <span className="text-red-500">*</span>
              </label>
              <select
                name="facilityType"
                value={inputs.facilityType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all ${
                  inputs.facilityType === 'hospital' 
                    ? 'border-green-500 bg-green-50 focus:ring-green-500' 
                    : 'border-blue-500 bg-blue-50 focus:ring-blue-500'
                }`}
              >
                <option value="general">🏭 General Industry</option>
                <option value="hospital">🏥 Academic Hospital</option>
              </select>
              <p className="text-xs mt-1 font-medium">
                {inputs.facilityType === 'hospital' ? (
                  <span className="text-green-700">✓ Hospital mode enabled - Fire safety & environmental calculations active</span>
                ) : (
                  <span className="text-blue-700">General industry mode - Standard calculations</span>
                )}
              </p>
            </div>

            {inputs.facilityType === 'hospital' && (
              <>
                <Input
                  label="Patient Beds"
                  type="number"
                  name="patientBeds"
                  value={inputs.patientBeds}
                  onChange={handleChange}
                  placeholder="Number of beds"
                />

                <Input
                  label="Square Footage"
                  type="number"
                  name="squareFootage"
                  value={inputs.squareFootage}
                  onChange={handleChange}
                  placeholder="Total sq ft"
                />

                <Input
                  label="Building Count"
                  type="number"
                  name="buildingCount"
                  value={inputs.buildingCount}
                  onChange={handleChange}
                  placeholder="Number of buildings"
                />

                <Input
                  label="Lab Count"
                  type="number"
                  name="labCount"
                  value={inputs.labCount}
                  onChange={handleChange}
                  placeholder="Research/clinical labs"
                />

                <Input
                  label="Emission Sources"
                  type="number"
                  name="emissionSources"
                  value={inputs.emissionSources}
                  onChange={handleChange}
                  placeholder="Generators, sterilizers"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facility Complexity</label>
                  <select
                    name="facilityComplexity"
                    value={inputs.facilityComplexity}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Basic Community Hospital</option>
                    <option value="2">Research/Teaching Hospital</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="border-t pt-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Annual Work Hours"
              type="number"
              name="totalHours"
              value={inputs.totalHours}
              onChange={handleChange}
            />

            <Input
              label="Leave Hours"
              type="number"
              name="leaveHours"
              value={inputs.leaveHours}
              onChange={handleChange}
            />

            <Input
              label="Admin Time %"
              type="number"
              name="adminPercent"
              value={inputs.adminPercent}
              onChange={handleChange}
            />

            <Input
              label="Core Program Hours"
              type="number"
              name="coreHours"
              value={inputs.coreHours}
              onChange={handleChange}
            />

            <Input
              label="Logistics Hours"
              type="number"
              name="logisticsHours"
              value={inputs.logisticsHours}
              onChange={handleChange}
            />

            <Input
              label="Employee Count"
              type="number"
              name="employeeCount"
              value={inputs.employeeCount}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="border-t pt-4 mt-4">
          <h3 className="font-semibold text-gray-900 mb-3">💰 Cost Parameters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Average Salary"
              type="number"
              name="avgSalary"
              value={inputs.avgSalary}
              onChange={handleChange}
            />

            <Input
              label="Benefits %"
              type="number"
              name="benefitsPercent"
              value={inputs.benefitsPercent}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="text-sm text-gray-600 bg-blue-50 p-4 rounded leading-relaxed">
          <strong>Note:</strong> Changes to global assumptions are automatically reflected in all calculations.
          {inputs.facilityType === 'hospital' ? (
            <>
              <br />
              <span className="inline-block mt-2">For hospitals: Employee count + patient beds scale workload. Fire safety and environmental hours are calculated automatically.</span>
            </>
          ) : (
            <>
              <br />
              <span className="inline-block mt-2">Employee count scales activity workload (1000 = baseline).</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalAssumptions;
